import React, { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { MeterSizeType } from "@/types/meter"
import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteMeterSize } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { MeterSizeForm } from "@/components/forms/meter-size-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface MeterSizeProps {
  data: MeterSizeType
}
export function MeterSizeOperation({ data }: MeterSizeProps) {
  const [showDropDown, setShowDropDown] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  return (
    <>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowDropDown(false)
              setShowEditModal(!showEditModal)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Meter Size
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDropDown(false)
              setShowDeleteModal(!showDeleteModal) // Open modal
            }}
          >
            <Icons.delete className="mr-2 size-4" />
            Delete Meter Size
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteMeterSizeModal
        meterSizeId={data.id}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
      />
      <EditMeterSizeModal
        MeterSizeInfo={data}
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
      />
    </>
  )
}

function EditMeterSizeModal({
  MeterSizeInfo,
  showEditModal,
  setShowEditModal,
}: {
  MeterSizeInfo: MeterSizeType
  showEditModal: boolean
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  return (
    <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
      <CredenzaContent className="w-full md:min-w-[600px]">
        <CredenzaTitle>
          <Header>Edit Meter Size</Header>
          <hr className="my-2" />
          <CredenzaBody className="py-2">
            <MeterSizeForm
              setShowMeterSizeModal={setShowEditModal}
              editData={MeterSizeInfo}
              onClose={() => setShowEditModal(false)}
            />
          </CredenzaBody>
        </CredenzaTitle>
      </CredenzaContent>
    </Credenza>
  )
}

function DeleteMeterSizeModal({
  meterSizeId,
  showDeleteModal,
  setShowDeleteModal,
}: {
  meterSizeId: number
  showDeleteModal: boolean
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const formSchema = z.object({
    id: z.coerce
      .number()
      .min(1, "Please enter the id to delete the meter size")
      .refine((val) => val === meterSizeId, {
        message: "Input ID type must match the Meter Size ID.",
      }),
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
  })

  const mutation = useMutation({
    mutationFn: async () => {
      await deleteMeterSize(meterSizeId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["metersize"] })
      toast({
        description: "Meter size deleted successfully.",
      })
      setShowDeleteModal(false)
    },
  })

  function handleDelete() {
    if (!validateUserDeletePermission()) return
    mutation.mutate()
  }

  return (
    <Credenza open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(handleDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Meter Size</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type "{meterSizeId}" to delete.</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""} // Allow empty input
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
